import { useState } from 'react';
import { useSnackbar } from 'notistack';

import { useTranslate } from 'src/locales';
import { useSetCandidateProfileMutation } from 'src/services/candidates/candidates.service';
import { generateCandidateProfileErrorMessage } from 'src/services/candidates/candidates.utils';
import {
  CandidateProfile,
  WriteBackConflict,
  WriteBackFieldKey,
  SetCandidateProfileResponse,
} from 'src/services/candidates/candidates.types';

import WriteBackConfirmDialog from './write-back-confirm-dialog';

type SaveArgs = { id: string; data: Partial<CandidateProfile> & { apply_write_back?: WriteBackFieldKey[] } };

/**
 * JT1-1534: wraps the candidate-profile save so an edit on an application's attached profile that
 * differs from the recruiter's own-org CRM record surfaces a confirm dialog; confirming re-issues
 * the save with `apply_write_back`. Produces no dialog for edits the API reports no conflicts for
 * (CRM-record edits, job-seeker edits, empty CRM auto-fills, or matching values), so it can be
 * dropped into any boxed editor. Render the returned `dialog` and call `save` in place of the raw
 * mutation. The primary save always resolves first — the CRM confirm is a follow-up.
 */
export function useCandidateProfileWriteBack() {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [updateProfile] = useSetCandidateProfileMutation();

  const [conflicts, setConflicts] = useState<WriteBackConflict[]>([]);
  const [pending, setPending] = useState<SaveArgs | null>(null);
  const [loading, setLoading] = useState(false);

  const isOpen = pending !== null && conflicts.length > 0;

  const close = () => {
    setConflicts([]);
    setPending(null);
    setLoading(false);
  };

  // Primary save. Resolves with the response; opens the confirm dialog if the API reported conflicts.
  const save = async (args: SaveArgs): Promise<SetCandidateProfileResponse> => {
    const response = await updateProfile(args).unwrap();
    const responseConflicts = response?.write_back?.conflicts ?? [];
    if (responseConflicts.length > 0) {
      setPending(args);
      setConflicts(responseConflicts);
    }
    return response;
  };

  const onConfirm = async (fields: WriteBackFieldKey[]) => {
    if (!pending) return;
    setLoading(true);
    try {
      await updateProfile({ ...pending, data: { ...pending.data, apply_write_back: fields } }).unwrap();
      enqueueSnackbar(t('profile.write_back.success'), { variant: 'success' });
      close();
    } catch (error) {
      enqueueSnackbar(generateCandidateProfileErrorMessage(error), { variant: 'error' });
      setLoading(false);
    }
  };

  const dialog = (
    <WriteBackConfirmDialog
      open={isOpen}
      conflicts={conflicts}
      loading={loading}
      onConfirm={onConfirm}
      onCancel={close}
    />
  );

  return { save, dialog };
}
